import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  isProgrammaticScroll,
  logScroll,
  runProgrammaticScroll,
  summarizeScroll,
} from '@teo/widget-core';

const PIN_THRESHOLD_PX = 96;
const CONTENT_RESIZE_DEBOUNCE_MS = 120;
const RESIZE_OBSERVER_DEBOUNCE_MS = 80;
/** After open, wait for lazy media/layout before revealing the list. */
const OPEN_SETTLE_DEBOUNCE_MS = 160;
const OPEN_SETTLE_MAX_MS = 900;

export function isPinnedToBottom(
  container: HTMLElement,
  thresholdPx = PIN_THRESHOLD_PX,
): boolean {
  const distance =
    container.scrollHeight - container.scrollTop - container.clientHeight;
  return distance <= thresholdPx;
}

/** Single instant jump — skips when already pinned (within 1px). */
export function scrollContainerToEndInstant(
  container: HTMLElement,
  reason = 'instant',
): void {
  runProgrammaticScroll(reason, () => {
    const before = summarizeScroll(container);
    if (before && before.distance <= 1) {
      logScroll('apply', `${reason} — skip, already at end (dist ${before.distance})`, { before }, 'debug');
      return;
    }
    container.scrollTop = container.scrollHeight;
    const after = summarizeScroll(container);
    logScroll(
      'apply',
      `${reason} — scrollTop ${before?.scrollTop ?? '?'}→${after?.scrollTop ?? '?'} (dist ${before?.distance ?? '?'}→${after?.distance ?? '?'}, h ${before?.scrollHeight ?? '?'}→${after?.scrollHeight ?? '?'})`,
      { before, after },
    );
  });
}

type UseChatScrollOptions = {
  messageCount: number;
  isTyping: boolean;
  forcePin?: boolean;
  /** Chat panel visible (not collapsed). */
  visible?: boolean;
};

export function useChatScroll({
  messageCount,
  isTyping,
  forcePin = false,
  visible = true,
}: UseChatScrollOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  /** Inner column — ResizeObserver on scrollport alone misses child height growth. */
  const contentRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);
  const wasVisibleRef = useRef(visible);
  const lastPinnedCountRef = useRef(messageCount);
  const prevMessageCountRef = useRef(0);
  const lastScrollTopRef = useRef(0);
  const contentResizeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const openSettlingRef = useRef(false);
  const openSettleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const openSettleMaxTimerRef = useRef<ReturnType<typeof setTimeout>>();
  /** First open after load (prefetch + lazy media); reopen is instant. */
  const initialOpenSettleDoneRef = useRef(false);
  /** Tracks whether chat has ever been opened in this tab session. */
  const hasEverOpenedRef = useRef(false);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [listVisible, setListVisible] = useState(false);

  const syncPreparingClass = useCallback((preparing: boolean) => {
    const container = containerRef.current;
    if (!container) return;
    container.classList.toggle('teo-messages--preparing', preparing);
  }, []);

  const clearOpenSettleTimers = useCallback(() => {
    if (openSettleTimerRef.current) {
      clearTimeout(openSettleTimerRef.current);
      openSettleTimerRef.current = undefined;
    }
    if (openSettleMaxTimerRef.current) {
      clearTimeout(openSettleMaxTimerRef.current);
      openSettleMaxTimerRef.current = undefined;
    }
  }, []);

  const logContainer = useCallback(
    (scope: string, message: string, extra?: Record<string, unknown>) => {
      logScroll(scope, message, {
        messageCount,
        isTyping,
        forcePin,
        visible,
        pinnedRef: pinnedRef.current,
        listVisible,
        metrics: summarizeScroll(containerRef.current),
        ...extra,
      });
    },
    [forcePin, isTyping, listVisible, messageCount, visible],
  );

  const revealListIfPinned = useCallback(
    (reason: string) => {
      if (openSettlingRef.current) {
        syncPreparingClass(true);
        setListVisible(false);
        logContainer('reveal', `deferred — open settling (${reason})`);
        return false;
      }

      const container = containerRef.current;
      const empty = messageCount === 0 && !isTyping;
      const scrolledUp =
        !empty &&
        !forcePin &&
        !pinnedRef.current &&
        container != null &&
        !isPinnedToBottom(container);

      if (scrolledUp) {
        syncPreparingClass(false);
        setListVisible(true);
        logContainer('reveal', `visible while scrolled up (${reason})`, {
          metrics: summarizeScroll(container),
        });
        return false;
      }

      const pinned =
        empty ||
        forcePin ||
        (container != null && isPinnedToBottom(container));

      syncPreparingClass(!pinned);
      setListVisible(pinned);

      logContainer(
        'reveal',
        pinned ? `ready (${reason})` : `waiting for pin (${reason})`,
        {
          empty,
          forcePin,
          metrics: summarizeScroll(container),
        },
      );

      return pinned;
    },
    [forcePin, isTyping, logContainer, messageCount, syncPreparingClass],
  );

  const finishOpenSettle = useCallback(
    (reason: string) => {
      if (!openSettlingRef.current) return;

      openSettlingRef.current = false;
      clearOpenSettleTimers();

      initialOpenSettleDoneRef.current = true;

      const container = containerRef.current;
      if (
        container &&
        visible &&
        (forcePin || pinnedRef.current) &&
        (messageCount > 0 || isTyping)
      ) {
        scrollContainerToEndInstant(container, `settle:${reason}`);
        lastScrollTopRef.current = container.scrollTop;
      }

      revealListIfPinned(`settle:${reason}`);
      logContainer('settle', `open settle complete (${reason})`);
    },
    [
      clearOpenSettleTimers,
      forcePin,
      isTyping,
      logContainer,
      messageCount,
      revealListIfPinned,
      visible,
    ],
  );

  const scheduleOpenSettle = useCallback(
    (reason: string) => {
      if (!openSettlingRef.current) return;

      if (openSettleTimerRef.current) {
        clearTimeout(openSettleTimerRef.current);
      }

      openSettleTimerRef.current = setTimeout(() => {
        openSettleTimerRef.current = undefined;
        finishOpenSettle(`debounce:${reason}`);
      }, OPEN_SETTLE_DEBOUNCE_MS);

      if (!openSettleMaxTimerRef.current) {
        openSettleMaxTimerRef.current = setTimeout(() => {
          finishOpenSettle('max-wait');
        }, OPEN_SETTLE_MAX_MS);
      }

      logContainer('settle', `scheduled (${reason})`, {
        debounceMs: OPEN_SETTLE_DEBOUNCE_MS,
        maxMs: OPEN_SETTLE_MAX_MS,
      });
    },
    [finishOpenSettle, logContainer],
  );

  const startOpenSettle = useCallback(
    (reason: string) => {
      openSettlingRef.current = true;
      syncPreparingClass(true);
      setListVisible(false);
      scheduleOpenSettle(reason);
      logContainer('settle', `start (${reason})`);
    },
    [logContainer, scheduleOpenSettle, syncPreparingClass],
  );

  const pinToBottom = useCallback(
    (reason: string) => {
      const container = containerRef.current;
      if (!container) return;

      const shouldPin = forcePin || pinnedRef.current;
      if (!shouldPin) return;

      if (messageCount > 0 || isTyping) {
        scrollContainerToEndInstant(container, reason);
        lastScrollTopRef.current = container.scrollTop;
      }
    },
    [forcePin, isTyping, messageCount],
  );

  const syncPinnedState = useCallback(
    (container: HTMLElement) => {
      const pinned = isPinnedToBottom(container);
      const prevPinned = pinnedRef.current;
      pinnedRef.current = pinned;
      setPinnedToBottom(pinned);
      if (pinned) {
        lastPinnedCountRef.current = messageCount;
        setNewMessageCount(0);
      }
      if (prevPinned !== pinned) {
        logScroll('pin', pinned ? 'pinned to bottom' : 'unpinned from bottom', {
          messageCount,
          metrics: summarizeScroll(container),
        });
      }
      return pinned;
    },
    [messageCount],
  );

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const container = containerRef.current;
      if (!container) {
        logScroll('scrollToBottom', 'skipped — no container');
        return;
      }

      logContainer('scrollToBottom', `invoke (${behavior})`);

      runProgrammaticScroll(`scrollToBottom:${behavior}`, () => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior,
        });
      });

      pinnedRef.current = true;
      setPinnedToBottom(true);
      lastPinnedCountRef.current = messageCount;
      setNewMessageCount(0);

      if (behavior === 'auto') {
        lastScrollTopRef.current = container.scrollTop;
      }
    },
    [logContainer, messageCount],
  );

  const maybeScrollToEnd = useCallback(
    (force = false, reason = 'maybeScrollToEnd') => {
      const container = containerRef.current;
      if (!container || !visible) return;

      const willScroll = force || forcePin || pinnedRef.current;
      logContainer(reason, willScroll ? 'scroll' : 'skip', {
        force,
        willScroll,
        lastPinnedCount: lastPinnedCountRef.current,
      });

      if (willScroll) {
        scrollContainerToEndInstant(container, reason);
        lastScrollTopRef.current = container.scrollTop;
        if (force || forcePin) {
          pinnedRef.current = true;
          setPinnedToBottom(true);
          lastPinnedCountRef.current = messageCount;
          setNewMessageCount(0);
        }
        revealListIfPinned(reason);
      } else if (messageCount > lastPinnedCountRef.current) {
        const delta = messageCount - lastPinnedCountRef.current;
        setNewMessageCount(delta);
        logContainer(reason, 'new messages while scrolled up', { delta });
      }
    },
    [forcePin, logContainer, messageCount, revealListIfPinned, visible],
  );

  useLayoutEffect(() => {
    const justOpened = visible && !wasVisibleRef.current;
    wasVisibleRef.current = visible;

    if (!visible) {
      openSettlingRef.current = false;
      clearOpenSettleTimers();
      // Don't force-hide the list on close — window is `display:none` anyway.
      // Forcing `teo-messages--preparing` here causes a gray/empty flash on reopen
      // (first paint happens before this hook's layout effect can unhide it).
      if (hasEverOpenedRef.current) {
        syncPreparingClass(false);
        setListVisible(true);
      }
      return;
    }

    const countIncreased = messageCount > prevMessageCountRef.current;
    prevMessageCountRef.current = messageCount;

    if (justOpened) {
      const isReopen = hasEverOpenedRef.current;
      hasEverOpenedRef.current = true;

      if (isReopen) {
        // Reopen must be instant (keep previous scroll position), never hide list.
        openSettlingRef.current = false;
        clearOpenSettleTimers();
        syncPreparingClass(false);
        setListVisible(true);
        logContainer('reveal', 'ready (layout:reopen)', {
          metrics: summarizeScroll(containerRef.current),
        });
      }

      if (
        !isReopen &&
        !initialOpenSettleDoneRef.current &&
        (messageCount > 0 || isTyping)
      ) {
        startOpenSettle('layout:open');
      }
    }

    const shouldPin =
      justOpened ||
      forcePin ||
      isTyping ||
      pinnedRef.current;

    if (shouldPin && (messageCount > 0 || isTyping)) {
      const reason = justOpened
        ? 'layout:open'
        : forcePin
          ? 'layout:force-pin'
          : isTyping
            ? 'layout:typing'
            : 'layout:pinned';
      pinToBottom(reason);

      if (justOpened) {
        const container = containerRef.current;
        if (container && !isPinnedToBottom(container)) {
          requestAnimationFrame(() => {
            pinToBottom('layout:open-rAF');
            if (!initialOpenSettleDoneRef.current && openSettlingRef.current) {
              scheduleOpenSettle('layout:open-rAF');
            }
          });
        }
      }
    }

    if (!justOpened) {
      revealListIfPinned('layout:visible');
    } else if (messageCount === 0 && !isTyping) {
      initialOpenSettleDoneRef.current = true;
      revealListIfPinned('layout:open-empty');
    }

    logContainer('layout', 'after open/visible pass', {
      justOpened,
      countIncreased,
      forcePin,
      isTyping,
      shouldPin,
      openSettling: openSettlingRef.current,
      initialOpenSettleDone: initialOpenSettleDoneRef.current,
    });
  }, [
    visible,
    messageCount,
    isTyping,
    forcePin,
    pinToBottom,
    logContainer,
    revealListIfPinned,
    scheduleOpenSettle,
    startOpenSettle,
    syncPreparingClass,
    clearOpenSettleTimers,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onScroll = () => {
      const metrics = summarizeScroll(container);
      const scrollTop = metrics?.scrollTop ?? 0;
      const delta = Math.abs(scrollTop - lastScrollTopRef.current);
      lastScrollTopRef.current = scrollTop;

      if (delta >= 2) {
        logScroll(
          'scroll-event',
          isProgrammaticScroll() ? 'programmatic move' : 'user/layout move',
          { delta, metrics, messageCount },
          'debug',
        );
      }

      syncPinnedState(container);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [messageCount, syncPinnedState]);

  useEffect(() => {
    if (!visible) return;
    const container = containerRef.current;
    if (!container) return;

    // Layout effect already pins on open / messageCount change.
    if (isPinnedToBottom(container)) {
      revealListIfPinned('effect:already-pinned');
      logContainer('deps', 'already pinned — skip effect scroll', {
        lastPinnedCount: lastPinnedCountRef.current,
      });
      return;
    }

    logContainer('deps', 'messageCount / isTyping / forcePin changed', {
      lastPinnedCount: lastPinnedCountRef.current,
    });
    maybeScrollToEnd(forcePin, 'effect:messages');
  }, [forcePin, isTyping, logContainer, maybeScrollToEnd, messageCount, revealListIfPinned, visible]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let resizeRaf = 0;
    let resizeTimer: ReturnType<typeof setTimeout> | undefined;
    let resizeSeq = 0;

    const applyResize = (seq: number) => {
      logScroll('resize', 'container/content resized', {
        seq,
        pinnedRef: pinnedRef.current,
        forcePin,
        messageCount,
        metrics: summarizeScroll(container),
      });
      if (pinnedRef.current || forcePin) {
        if (!isPinnedToBottom(container)) {
          scrollContainerToEndInstant(container, `resize:${seq}`);
          lastScrollTopRef.current = container.scrollTop;
        } else {
          logScroll('resize', `resize:${seq} — skip, already pinned`, {
            seq,
            metrics: summarizeScroll(container),
          }, 'debug');
        }
        if (openSettlingRef.current) {
          scheduleOpenSettle(`resize:${seq}`);
        } else if (visible) {
          revealListIfPinned(`resize:${seq}`);
        }
      }
    };

    const ro = new ResizeObserver(() => {
      if (!visible) return;
      const seq = ++resizeSeq;
      const pinNow = pinnedRef.current || forcePin;
      // Pinned: coalesce to one rAF (not setTimeout(0) storms from composer
      // autosize / media). Unpinned: keep debounce.
      if (pinNow) {
        if (resizeRaf) cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(() => {
          resizeRaf = 0;
          applyResize(seq);
        });
        return;
      }
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resizeTimer = undefined;
        applyResize(seq);
      }, RESIZE_OBSERVER_DEBOUNCE_MS);
    });

    ro.observe(container);
    const content = contentRef.current;
    if (content) ro.observe(content);
    return () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      if (resizeTimer) clearTimeout(resizeTimer);
      ro.disconnect();
    };
  }, [forcePin, messageCount, revealListIfPinned, scheduleOpenSettle, visible]);

  const onContentResize = useCallback(() => {
    if (!visible) return;

    if (openSettlingRef.current) {
      logContainer('content-resize', 'during open settle');
      const container = containerRef.current;
      if (container && (pinnedRef.current || forcePin)) {
        scrollContainerToEndInstant(container, 'content-resize:settle');
        lastScrollTopRef.current = container.scrollTop;
      }
      scheduleOpenSettle('content-resize');
      return;
    }

    const pinNow = pinnedRef.current || forcePin;
    logContainer(
      'content-resize',
      pinNow ? 'media/layout loaded (immediate pin)' : 'media/layout loaded (debounced)',
    );

    if (contentResizeTimerRef.current) {
      clearTimeout(contentResizeTimerRef.current);
    }

    contentResizeTimerRef.current = setTimeout(() => {
      contentResizeTimerRef.current = undefined;
      maybeScrollToEnd(forcePin, 'content-resize:batched');
    }, pinNow ? 0 : CONTENT_RESIZE_DEBOUNCE_MS);
  }, [forcePin, logContainer, maybeScrollToEnd, scheduleOpenSettle, visible]);

  useEffect(() => {
    return () => {
      if (contentResizeTimerRef.current) {
        clearTimeout(contentResizeTimerRef.current);
      }
      clearOpenSettleTimers();
    };
  }, [clearOpenSettleTimers]);

  return {
    containerRef,
    contentRef,
    pinnedToBottom,
    newMessageCount,
    listVisible,
    scrollToBottom,
    onContentResize,
  };
}
